/**
 * Loads and body weight in the person's unit (`D266`), for the screens.
 *
 * Every function takes KILOGRAMS — what the server sends and the stores
 * hold — and answers in the unit the person chose (`stores/weight-unit.ts`).
 * The arithmetic is `@repo/client/format`'s `weight.ts`, tested there; this
 * file only binds it to the app's locale and unit.
 *
 * ── A MESSAGE NEVER SPELLS THE UNIT ────────────────────────────────────────
 * A sentence that quotes a load takes it whole, unit included, from
 * {@link loadText} — `"{load} × {reps}"`, never `"{load} kg × {reps}"`. A
 * message that wrote "kg" itself would print "kg" beside a pound figure.
 */
import {
  type FormatWeightOptions,
  formatVolume,
  formatWeight,
  formatWeightNumber,
  fromWeightUnit,
  loadStepInUnit,
  maxLoadInUnit,
  toWeightUnit,
} from "@repo/client/format";
import type { WeightUnit } from "@repo/contract";
import { weightUnit } from "../stores/weight-unit";
import { appLocale } from "./locale";

/** "kg" or "lb" — the unit word beside a field or a dial. */
export function unitSymbol(unit: WeightUnit = weightUnit()): string {
  return unit;
}

/** A load as a bare number in the person's unit: "72,5" / "160". */
export function loadNumber(
  kg: number,
  options: FormatWeightOptions = { maxFractionDigits: 2 }
): string {
  return formatWeightNumber(kg, weightUnit(), appLocale(), options);
}

/** A load with its unit: "72,5 kg" / "160 lb". */
export function loadText(
  kg: number,
  options: FormatWeightOptions = { maxFractionDigits: 2 }
): string {
  return formatWeight(kg, weightUnit(), appLocale(), options);
}

/** A signed change of load with its unit: "+2,5 kg" / "−5 lb". */
export function loadDeltaText(
  kg: number,
  options: FormatWeightOptions = { maxFractionDigits: 2 }
): string {
  let sign = "";
  if (kg > 0) {
    sign = "+";
  } else if (kg < 0) {
    sign = "−";
  }
  return `${sign}${loadText(Math.abs(kg), options)}`;
}

/** A training volume with its unit: "2,2 t" / "4,850 lb". */
export function volumeText(kg: number): string {
  return formatVolume(kg, weightUnit(), appLocale());
}

/** A stored kilogram value in the person's unit, as a number (0.01). */
export function toUnit(kg: number): number {
  return toWeightUnit(kg, weightUnit());
}

/** A value the person dialled or typed, back to kilograms. */
export function fromUnit(value: number): number {
  return fromWeightUnit(value, weightUnit());
}

/** The dial's step in the person's unit for a plan step in kilograms. */
export function stepInUnit(stepKg: number): number {
  return loadStepInUnit(stepKg, weightUnit());
}

/** The dial's ceiling in the person's unit. */
export function maxInUnit(maxKg: number): number {
  return maxLoadInUnit(maxKg, weightUnit());
}
